import { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import api, { apiError, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { Logo, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { format } from "date-fns";
import { toast } from "sonner";
import { istNow } from "../lib/dates";
import { CustomerFullHistory } from "../components/CustomerFullHistory";
import {
  Plus, Minus, Clock, Trash2, Printer, MessageSquare, Save, FileText, History,
  ArrowLeft, User, CheckCircle2, Package, Eye, CreditCard,
  Calendar as CalendarIcon, Check, ListChecks, Copy, Gift, Pencil, Send, AlertTriangle,
} from "lucide-react";

const MARKETING_SOURCES = ["Walk-in", "Pamphlet", "Facebook / Instagram", "Google", "Existing Client Referral", "Staff Referral", "Friend / Family", "Website / Online Booking", "Other"];

const blankRow = () => ({ type: "service", service_id: "", product_id: "", name: "", category: "", price: 0, actual_price: 0, duration: 0, qty: 1, staff_id: "", discount_type: "amount", discount_value: 0, incentive_eligible: true, editable_price: false });
const blankProductRow = () => ({ type: "product", service_id: "", product_id: "", name: "", category: "", price: 0, actual_price: 0, duration: 0, qty: 1, staff_id: "", discount_type: "amount", discount_value: 0, incentive_eligible: false });
const lineCalc = (it) => {
  const gross = Number(it.price || 0) * Number(it.qty || 1);
  const dval = Number(it.discount_value || 0);
  let disc = it.discount_type === "percent" ? gross * Math.min(Math.max(dval, 0), 100) / 100 : Math.min(Math.max(dval, 0), gross);
  disc = Math.min(disc, gross);
  return { gross, disc, net: Math.max(gross - disc, 0) };
};

export default function BillingPOS() {
  const { user } = useAuth();
  const { branches, selected } = useBranch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const todayStr = format(istNow(), "yyyy-MM-dd");
  const canBackdate = isAdmin || !!user?.permissions?.allow_backdate_billing;
  const applyBackdate = (d) => {
    const v = d ? format(d, "yyyy-MM-dd") : "";
    setBillDate(v);
    if (v && v !== todayStr) localStorage.setItem("pos_backdate", v);
    else localStorage.removeItem("pos_backdate");
  };
  const [draftId, setDraftId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [appointmentIds, setAppointmentIds] = useState([]);
  const [bookingId, setBookingId] = useState(null);

  const [branchId, setBranchId] = useState("");
  const [staff, setStaff] = useState([]);
  const [extraStaff, setExtraStaff] = useState([]);
  const [rows, setRows] = useState([blankRow()]);
  const [client, setClient] = useState({ id: null, name: "", mobile: "" });
  const [history, setHistory] = useState(null);
  const [overallDiscount, setOverallDiscount] = useState(0);
  const [gst, setGst] = useState(5);
  const [payments, setPayments] = useState([{ method: "Cash", amount: "" }]);
  const [billDate, setBillDate] = useState("");
  const [billTime, setBillTime] = useState("");
  const [editReason, setEditReason] = useState("");
  const [reasonOpen, setReasonOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [staffErrorRows, setStaffErrorRows] = useState([]);
  const [referralCode, setReferralCode] = useState("");
  const [referralInfo, setReferralInfo] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [marketingSource, setMarketingSource] = useState("");
  const [sourceStatus, setSourceStatus] = useState({ needs_source: false, marketing_source: null });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [searching, setSearching] = useState(false);
  const [nameSug, setNameSug] = useState([]);
  const [showNameSug, setShowNameSug] = useState(false);
  const [nameSearching, setNameSearching] = useState(false);
  const [svcPickerOpen, setSvcPickerOpen] = useState(false);
  const [fp, setFp] = useState({ enabled: false, slabs: [], services: [] });
  const [fpOpen, setFpOpen] = useState(false);
  const [comboPkgs, setComboPkgs] = useState([]);
  const [pkgPickerOpen, setPkgPickerOpen] = useState(false);
  const [fpSlab, setFpSlab] = useState(null);
  const [fpSel, setFpSel] = useState({});
  const [fpQ, setFpQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editCustId, setEditCustId] = useState(null);
  const [newCust, setNewCust] = useState({ name: "", mobile: "", whatsapp: "", email: "", gender: "", dob: "", address: "", notes: "", preferred_branch: null, preferred_staff: null, marketing_consent: true, whatsapp_optin: true, membership_id: null });

  useEffect(() => {
    const b = (selected && selected !== "all") ? selected : (user?.branch_id || branches[0]?.id || "");
    if (b) setBranchId(b);
  }, [selected, branches, user]);

  useEffect(() => {
    if (!branchId) return;
    api.get("/staff", { params: { branch_id: branchId } }).then((r) => setStaff(r.data));
    api.get("/freedom-package", { params: { branch_id: branchId } }).then((r) => setFp(r.data)).catch(() => {});
    api.get("/combo-packages").then((r) => setComboPkgs((r.data || []).filter((p) => p.status === "active"))).catch(() => {});
  }, [branchId]);

  // ---- hydrate an active back-date (persists across multiple new bills) ----
  useEffect(() => {
    if (location.state?.editId || location.state?.draftId || !canBackdate) return;
    const saved = localStorage.getItem("pos_backdate");
    if (saved && saved !== todayStr) setBillDate(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- default GST for NEW bills from the saved GST setting (not when editing a draft/invoice) ----
  useEffect(() => {
    if (location.state?.editId || location.state?.draftId) return;
    api.get("/settings/gst").then(({ data }) => {
      if (data && data.total != null) setGst(Number(data.total));
    }).catch(() => { /* keep default */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // ---- load a saved draft to convert, or a finalized invoice to edit ----
  useEffect(() => {
    const did = location.state?.draftId;
    const eid = location.state?.editId;
    const id = did || eid;
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${id}`);
        if (did) setDraftId(did);
        if (eid) setEditId(eid);
        if (data.branch_id) setBranchId(data.branch_id);
        setClient({ id: data.customer_id || null, name: data.customer_name === "Walk-in" ? "" : (data.customer_name || ""), mobile: data.customer_mobile || "" });
        if (data.customer_id) { try { const c = await api.get(`/customers/${data.customer_id}`); setHistory(c.data); } catch { /* ignore */ } }
        setGst(Number(data.tax_percent || 0));
        setOverallDiscount(Number(data.discount || 0));
        const rws = (data.items || []).map((it) => ({
          type: it.type || "service", service_id: it.service_id || "", product_id: it.product_id || "",
          name: it.name, category: it.category || "", price: Number(it.original_price ?? it.price ?? 0),
          actual_price: Number(it.actual_price || it.original_price || it.price || 0),
          qty: Number(it.qty || it.quantity || 1), staff_id: it.staff_id || "",
          discount_type: it.discount_type || "amount", discount_value: Number(it.discount_value || 0),
          incentive_eligible: it.incentive_eligible !== false,
        }));
        if (rws.length) setRows(rws);
        if (eid && (data.payments || []).length) setPayments(data.payments.map((p) => ({ method: p.method, amount: String(p.amount) })));
        if (eid) { setBillDate(data.bill_date || ""); setBillTime(data.bill_time || ""); }
        toast.message(eid ? `Editing invoice ${data.invoice_no}` : `Draft ${data.invoice_no} loaded — review & convert`);
      } catch (e) { toast.error("Could not load invoice"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- prefill from an appointment ("Create Bill") ----
  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf) return;
    if (pf.branch_id) setBranchId(pf.branch_id);
    setAppointmentId(pf.appointmentId || null);
    setAppointmentIds(pf.appointmentIds || (pf.appointmentId ? [pf.appointmentId] : []));
    setBookingId(pf.booking_id || null);
    setClient({ id: pf.customer_id || null, name: pf.customer_name || "", mobile: pf.customer_mobile || "" });
    if (pf.customer_id) { api.get(`/customers/${pf.customer_id}`).then((c) => setHistory(c.data)).catch(() => { /* ignore */ }); }
    const list = pf.services && pf.services.length ? pf.services : (pf.service ? [{ ...pf.service, staff_id: pf.staff_id }] : []);
    setExtraStaff(list.map((s) => ({ id: s.staff_id, name: s.staff_name || "" })).filter((x) => x.id));
    if (list.length) {
      const built = list.map((s) => ({ ...blankRow(), type: "service", service_id: s.id, name: s.name, category: s.category || "",
        price: Number(s.actual_price || s.price || 0), actual_price: Number(s.actual_price || s.price || 0), duration: s.duration || 0,
        staff_id: s.staff_id || "", incentive_eligible: s.incentive_eligible !== false,
        freedom_group: s.freedom_group || null, freedom_slab_count: s.freedom_slab_count || null, freedom_slab_price: s.freedom_slab_price || null }));
      // Mirror the POS Freedom builder: distribute the slab price across each package group via
      // per-line discounts so the preview total matches (the backend re-prices authoritatively too).
      const fgroups = {};
      built.forEach((r, i) => { if (r.freedom_group) { (fgroups[r.freedom_group] = fgroups[r.freedom_group] || []).push(i); } });
      Object.values(fgroups).forEach((idxs) => {
        const slab = Number(built[idxs[0]].freedom_slab_price) || 0;
        const actuals = idxs.map((i) => Number(built[i].actual_price) || 0);
        const sum = actuals.reduce((a, b) => a + b, 0) || 1;
        let assigned = 0;
        idxs.forEach((i, k) => {
          const net = k < idxs.length - 1 ? Math.round(slab * actuals[k] / sum * 100) / 100 : Math.round((slab - assigned) * 100) / 100;
          assigned += net;
          built[i].discount_type = "amount";
          built[i].discount_value = Math.max((Number(built[i].actual_price) || 0) - net, 0);
        });
      });
      setRows(built);
    }
    toast.message("Appointment loaded — review & checkout");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branch = branches.find((b) => b.id === branchId);
  const referralActive = !!referralInfo && (!client.id || (history?.total_visits || 0) === 0);
  const calc = useMemo(() => {
    const subtotal = rows.reduce((s, it) => s + lineCalc(it).gross, 0);
    const itemDiscount = rows.reduce((s, it) => s + lineCalc(it).disc, 0);
    const actualTotal = rows.reduce((s, it) => s + Number(it.actual_price || it.price || 0) * Number(it.qty || 1), 0);
    const net = subtotal - itemDiscount;
    const couponDisc = couponInfo ? (couponInfo.discount_type === "percent" ? net * Number(couponInfo.discount_value) / 100 : Math.min(Number(couponInfo.discount_value), net)) : 0;
    const referralDisc = referralActive ? Math.round(net * (Number(referralInfo.discount_percent) || 0)) / 100 : 0;
    const grand = Math.max(net - Number(overallDiscount || 0) - couponDisc - referralDisc, 0);
    const rate = Number(gst || 0);
    const taxableValue = rate ? grand / (1 + rate / 100) : grand;
    const tax = grand - taxableValue;
    const savings = Math.max(actualTotal - grand, 0);
    return { subtotal, itemDiscount, actualTotal, net, couponDisc, referralDisc, tax, taxableValue, grand, savings };
  }, [rows, overallDiscount, gst, couponInfo, referralActive, referralInfo]);

  const received = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const walletAvail = Number(history?.wallet_balance || 0);
  const walletUsed = payments.filter((p) => p.method === "Wallet").reduce((s, p) => s + Number(p.amount || 0), 0);
  const walletOver = walletUsed > walletAvail + 0.001;
  const balance = calc.grand - received;
  const underpaid = calc.grand > 0 && balance > 0.01;

  // ---- row ops ----
  const setRow = (i, patch) => setRows((r) => { const c = [...r]; c[i] = { ...c[i], ...patch }; return c; });
  const pickService = (i, svc) => {
    setRow(i, { type: "service", service_id: svc.id, product_id: "", name: svc.name, category: svc.category, price: svc.price, actual_price: svc.actual_price || svc.price, duration: svc.duration || 0, incentive_eligible: svc.incentive_eligible !== false, editable_price: svc.editable_price === true });
  };
  const pickProduct = (i, p) => {
    setRow(i, { type: "product", product_id: p.id, service_id: "", name: p.name, category: p.category, price: p.price, actual_price: p.actual_price || p.price, duration: 0, incentive_eligible: p.incentive_eligible === true });
  };
  const addRow = () => setRows((r) => [...r, blankRow()]);
  const addProductRow = () => setRows((r) => [...r, blankProductRow()]);
  const addWalletTopup = () => {
    const v = window.prompt("Wallet top-up amount (₹):", "500");
    const amt = Number(v);
    if (!amt || amt <= 0) return;
    setRows((r) => [...r, { type: "wallet_topup", service_id: "", product_id: "", name: "Wallet Top-up", category: "Wallet", price: amt, actual_price: amt, duration: 0, qty: 1, staff_id: "", discount_type: "amount", discount_value: 0, incentive_eligible: false }]);
  };
  const addMembership = () => {
    const name = window.prompt("Membership name:", "Gold Membership");
    if (!name) return;
    const v = window.prompt(`Price for "${name}" (₹):`, "2999");
    const amt = Number(v);
    if (!amt || amt <= 0) return;
    setRows((r) => [...r, { type: "membership", service_id: "", product_id: "", name, category: "Membership", price: amt, actual_price: amt, duration: 0, qty: 1, staff_id: "", discount_type: "amount", discount_value: 0, incentive_eligible: false }]);
  };
  const delRow = (i) => setRows((r) => r.length > 1 ? r.filter((_, idx) => idx !== i) : [blankRow()]);
  const cloneRow = (i) => setRows((r) => { const c = [...r]; c.splice(i + 1, 0, { ...r[i], staff_id: "" }); return c; });
  const addServiceFromPicker = (svc) => {
    setRows((r) => {
      const newRow = { ...blankRow(), type: "service", service_id: svc.id, name: svc.name,
        category: svc.category, price: svc.price, actual_price: svc.actual_price || svc.price,
        duration: svc.duration || 0, incentive_eligible: svc.incentive_eligible !== false, editable_price: svc.editable_price === true };
      if (r.length === 1 && r[0].type === "service" && !r[0].service_id && !r[0].product_id) return [newRow];
      return [...r, newRow];
    });
  };
  const removeServiceFromPicker = (svcId) => setRows((r) => {
    let idx = -1;
    r.forEach((x, i) => { if (x.type === "service" && x.service_id === svcId) idx = i; });
    if (idx < 0) return r;
    const next = r.filter((_, k) => k !== idx);
    return next.length ? next : [blankRow()];
  });
  const svcCounts = rows.reduce((m, x) => { if (x.type === "service" && x.service_id) m[x.service_id] = (m[x.service_id] || 0) + 1; return m; }, {});

  // ---- Freedom Package ----
  const fpSlabObj = fp.slabs?.find((s) => s.count === fpSlab) || null;
  const fpSelIds = Object.keys(fpSel);
  const fpActualTotal = fpSelIds.reduce((s, id) => s + (Number(fp.services.find((x) => x.id === id)?.actual_price) || 0), 0);
  const toggleFpSel = (svc) => setFpSel((m) => { const n = { ...m }; if (n[svc.id] !== undefined) delete n[svc.id]; else n[svc.id] = ""; return n; });
  const fpStaffFor = (serviceId) => { const elig = staff.filter((st) => (st.services || []).includes(serviceId)); return elig.length ? elig : staff; };
  const openFreedom = () => { setFpSlab(fp.slabs?.length === 1 ? fp.slabs[0].count : null); setFpSel({}); setFpQ(""); setFpOpen(true); };
  const addPackageLine = (pkg) => {
    setRows((r) => [...r, { type: "package", service_id: "", product_id: "", name: pkg.name, category: "Package",
      price: Number(pkg.offer_price) || 0, actual_price: Number(pkg.actual_price) || 0, duration: pkg.duration || 0,
      qty: 1, staff_id: "", discount_type: "amount", discount_value: 0, incentive_eligible: true,
      package_id: pkg.id, package_service_names: (pkg.services || []).map((s) => s.name) }]);
    setPkgPickerOpen(false);
    toast.success(`${pkg.name} added`);
  };
  const addFreedom = () => {
    const slab = fp.slabs.find((s) => s.count === fpSlab);
    if (!slab) { toast.error("Choose a package slab first"); return; }
    const ids = Object.keys(fpSel);
    if (ids.length !== slab.count) { toast.error(`Select exactly ${slab.count} services for this package`); return; }
    if (ids.some((id) => !fpSel[id])) { toast.error("Select a staff for every service"); return; }
    const svcObjs = ids.map((id) => fp.services.find((s) => s.id === id)).filter(Boolean);
    const actuals = svcObjs.map((s) => Number(s.actual_price) || 0);
    const sum = actuals.reduce((a, b) => a + b, 0) || 1;
    const group = `FP-${Date.now()}`;
    let assigned = 0;
    const newRows = svcObjs.map((s, k) => {
      const net = k < svcObjs.length - 1 ? Math.round(slab.price * actuals[k] / sum * 100) / 100 : Math.round((slab.price - assigned) * 100) / 100;
      assigned += net;
      return { ...blankRow(), type: "service", service_id: s.id, name: s.name, category: s.category || "",
        price: actuals[k], actual_price: actuals[k], duration: s.duration || 0, staff_id: fpSel[s.id] || "",
        incentive_eligible: true, discount_type: "amount", discount_value: Math.max(actuals[k] - net, 0),
        freedom_group: group, freedom_slab_count: slab.count, freedom_slab_price: slab.price };
    });
    setRows((r) => { const base = (r.length === 1 && r[0].type === "service" && !r[0].service_id && !r[0].product_id) ? [] : r; return [...base, ...newRows]; });
    setFpOpen(false);
    toast.success(`Freedom Package added — Any ${slab.count} for ${rupee(slab.price)}`);
  };
  const freedomGroups = useMemo(() => {
    const m = {};
    rows.forEach((r) => { if (r.freedom_group) { (m[r.freedom_group] = m[r.freedom_group] || []).push(r); } });
    return Object.entries(m).map(([g, its]) => ({ g, count: its.length, slab: Number(its[0].freedom_slab_price) || 0,
      actual: its.reduce((s, x) => s + Number(x.actual_price || 0) * Number(x.qty || 1), 0), names: its.map((x) => x.name) }));
  }, [rows]);

  // ---- smart mobile search (live, 4+ digits) ----
  const selectCustomer = async (id) => {
    try {
      const { data } = await api.get(`/customers/${id}`);
      setClient({ id: data.id, name: data.name, mobile: data.mobile });
      setHistory(data);
      setSuggestions([]); setShowSuggest(false);
      setNameSug([]); setShowNameSug(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    const raw = (client.mobile || "").replace(/\D/g, "");
    if (client.id) return;
    if (raw.length < 4) { setSuggestions([]); setShowSuggest(false); return; }
    setSearching(true); setShowSuggest(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers/search", { params: { q: raw } });
        setSuggestions(data);
        if (raw.length === 10 && data.length === 1) selectCustomer(data[0].id);
      } catch (e) { /* ignore */ }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.mobile, client.id]);

  // coupon must be re-applied if the customer changes (one-time-use / personalised expiry depend on it)
  useEffect(() => { setCouponInfo(null); }, [client.id]);


  // ---- Marketing Source requirement (first-ever completed bill) ----
  useEffect(() => {
    const raw = (client.mobile || "").replace(/\D/g, "");
    if (!client.id && raw.length !== 10) { setSourceStatus({ needs_source: false, marketing_source: null }); setMarketingSource(""); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers/marketing-status", { params: { customer_id: client.id || undefined, mobile: raw || undefined } });
        setSourceStatus(data);
        setMarketingSource(data.marketing_source || "");
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, client.mobile]);

  // ---- smart client-name search (live, 3+ letters) ----
  useEffect(() => {
    const name = (client.name || "").trim();
    if (client.id) return;
    if (name.length < 3) { setNameSug([]); setShowNameSug(false); return; }
    setNameSearching(true); setShowNameSug(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers/search", { params: { q: name } });
        setNameSug(data);
      } catch (e) { /* ignore */ }
      finally { setNameSearching(false); }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.name, client.id]);

  const openAdd = () => {
    setEditCustId(null);
    setNewCust({ name: client.name || "", mobile: (client.mobile || "").replace(/\D/g, ""), whatsapp: "", email: "", gender: "", dob: "", address: "", notes: "", preferred_branch: null, preferred_staff: null, marketing_consent: true, whatsapp_optin: true, membership_id: null });
    setAddOpen(true); setShowSuggest(false);
  };
  const openEditCust = () => {
    const h = history || {};
    setEditCustId(client.id);
    setNewCust({
      name: h.name ?? client.name ?? "", mobile: h.mobile ?? client.mobile ?? "", whatsapp: h.whatsapp ?? "",
      email: h.email ?? "", gender: h.gender ?? "", dob: h.dob ?? "", address: h.address ?? "", notes: h.notes ?? "",
      preferred_branch: h.preferred_branch ?? null, preferred_staff: h.preferred_staff ?? null,
      marketing_consent: h.marketing_consent ?? true, whatsapp_optin: h.whatsapp_optin ?? true, membership_id: h.membership_id ?? null,
    });
    setAddOpen(true);
  };
  const saveNew = async () => {
    if (!newCust.name?.trim() || !newCust.mobile?.trim()) { toast.error("Name and mobile required"); return; }
    try {
      if (editCustId) {
        await api.put(`/customers/${editCustId}`, newCust);
        toast.success("Changes saved");
        setAddOpen(false); setEditCustId(null);
        await selectCustomer(editCustId);
      } else {
        const { data } = await api.post("/customers", { ...newCust, preferred_branch: branchId || null });
        setClient({ id: data.id, name: data.name, mobile: data.mobile });
        setHistory({ ...data, invoices: [] });
        setAddOpen(false); toast.success("Customer added");
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const staffOptions = useMemo(() => {
    const ids = new Set(staff.map((s) => s.id));
    return [...staff, ...extraStaff.filter((e) => e.id && !ids.has(e.id))];
  }, [staff, extraStaff]);
  const staffName = (id) => staffOptions.find((s) => s.id === id)?.name || "";

  const prevStaffId = (i) => (i > 0 ? (rows[i - 1].staff_id || "") : "");
  const copyPrevStaff = (i) => {
    const pid = prevStaffId(i);
    if (pid) { setRow(i, { staff_id: pid }); setStaffErrorRows((e) => e.filter((x) => x !== i)); }
  };
  const validateStaff = () => {
    const missing = rows.reduce((acc, r, i) => {
      if (r.type === "service" && r.service_id && !r.staff_id) acc.push(i);
      return acc;
    }, []);
    if (missing.length) {
      setStaffErrorRows(missing);
      toast.error("Please select staff for all services before creating the bill.");
      return false;
    }
    setStaffErrorRows([]);
    return true;
  };

  const summary = rows.filter((r) => r.service_id && r.staff_id).map((r) => ({
    staff: staffName(r.staff_id), service: r.name, net: lineCalc(r).net,
  }));

  const build = (status, reason) => ({
    branch_id: branchId,
    customer_id: client.id, customer_name: client.name || "Walk-in", customer_mobile: client.mobile,
    marketing_source: sourceStatus.needs_source ? (marketingSource || null) : null,
    items: rows.filter((r) => r.service_id || r.product_id || r.type === "wallet_topup" || r.type === "membership" || r.type === "package").map((r) => ({
      type: r.type || "service", name: r.name, service_id: r.service_id || null, product_id: r.product_id || null, category: r.category,
      qty: Number(r.qty), price: Number(r.price), actual_price: Number(r.actual_price || r.price),
      discount_type: r.discount_type,
      discount_value: Number(r.discount_value), staff_id: r.staff_id || null,
      staff_name: staffName(r.staff_id), incentive_eligible: r.incentive_eligible,
      freedom_group: r.freedom_group || null, freedom_slab_count: r.freedom_slab_count || null, freedom_slab_price: r.freedom_slab_price || null,
      package_id: r.package_id || null, package_service_names: r.package_service_names || [],
    })),
    discount: Number(overallDiscount || 0), tax_percent: Number(gst || 0),
    status,
    appointment_id: appointmentIds[0] || appointmentId || null,
    appointment_ids: appointmentIds.length ? appointmentIds : (appointmentId ? [appointmentId] : []),
    booking_id: bookingId || null,
    bill_date: billDate || null, bill_time: billTime || null, edit_reason: reason || null,
    referral_code: referralActive ? referralCode.trim().toUpperCase() : null,
    coupon_code: couponInfo ? couponInfo.code : null,
    payments: status === "draft" ? [] : payments.map((p) => ({ method: p.method, amount: Number(p.amount || 0) })),
  });

  const submit = async (status, reason) => {
    const payload = build(status, reason);
    const markCompleted = async () => {
      for (const id of appointmentIds) {
        try { await api.put(`/appointments/${id}/status`, { status: "Completed" }); } catch { /* ignore */ }
      }
    };
    if (!payload.items.length) { toast.error("Add at least one service or product"); return; }
    if (!branchId) { toast.error("Select a branch"); return; }
    if (status !== "draft" && sourceStatus.needs_source && !marketingSource) {
      toast.error("Please select a Marketing Source for this new customer");
      return;
    }
    if (!validateStaff()) return;
    setSaving(true);
    try {
      if (draftId && status !== "draft") {
        const { data } = await api.post(`/invoices/${draftId}/finalize`, payload);
        setCreated(data); toast.success(`Invoice ${data.invoice_no} generated`);
        await markCompleted();
      } else if (editId) {
        const { data } = await api.put(`/invoices/${editId}`, payload);
        setCreated(data); toast.success(`Invoice ${data.invoice_no} updated`);
      } else {
        const { data } = await api.post("/invoices", payload);
        if (status === "draft") { toast.success("Draft saved"); navigate("/billing"); }
        else {
          setCreated(data); toast.success(`Invoice ${data.invoice_no} generated`);
          await markCompleted();
        }
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const [restock, setRestock] = useState(null);
  const [restockQty, setRestockQty] = useState("");
  const submitRestock = async () => {
    if (!restock) return;
    const qty = Number(restockQty || 0);
    if (qty <= 0) { toast.error("Enter a quantity greater than 0"); return; }
    try {
      await api.post(`/inventory/products/${restock.id}/purchase`, { branch_id: branchId, quantity: qty, reason: "POS quick restock" });
      toast.success(`Restocked ${restock.name} (+${qty})`);
      setRestock(null); setRestockQty("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const printPdf = (inv) => {
    const token = localStorage.getItem("ds_token");
    fetch(`${API}/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const w = window.open(u, "_blank"); if (w) w.onload = () => w.print(); setTimeout(() => URL.revokeObjectURL(u), 30000); })
      .catch(() => toast.error("Could not generate PDF"));
  };
  const whatsapp = async (inv) => {
    try {
      const { data } = await api.get(`/invoices/${inv.id}/whatsapp`, { params: { base_url: process.env.REACT_APP_INVOICE_BASE_URL || window.location.origin } });
      if (data.wa_link) window.open(data.wa_link, "_blank");
      else { try { await navigator.clipboard.writeText(data.invoice_url || data.message); toast.success("Invoice link copied"); } catch { toast.message("Add a mobile number to share"); } }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [sendingApi, setSendingApi] = useState(false);
  const whatsappApi = async (inv) => {
    if (sendingApi) return;
    setSendingApi(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/whatsapp-api`, null, { params: { base_url: process.env.REACT_APP_INVOICE_BASE_URL || window.location.origin } });
      toast.success(data.simulated ? "Invoice queued via WhatsApp API (simulation mode)." : "Invoice sent successfully via WhatsApp API.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "WhatsApp API send failed"); }
    finally { setSendingApi(false); }
  };

  if (created) {
    return (
      <div className="max-w-xl mx-auto text-center py-16" data-testid="pos-success">
        <div className="h-16 w-16 rounded-full bg-primary/20 text-primary flex items-center justify-center mx-auto mb-4"><CheckCircle2 size={34} /></div>
        <h1 className="font-display text-3xl">Invoice {created.invoice_no}</h1>
        <p className="text-muted-foreground mt-1">{client.name || "Walk-in"} · {rupee(created.grand_total)}</p>
        {created.total_savings > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 text-green-300 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-1.5" data-testid="pos-success-savings">
            <span className="font-display text-lg">You Saved {rupee(created.total_savings)}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2 justify-center mt-6">
          <Button variant="outline" className="rounded-sm" data-testid="pos-print" onClick={() => printPdf(created)}><Printer size={15} className="mr-1" /> Print</Button>
          <Button variant="outline" className="rounded-sm" data-testid="pos-whatsapp" onClick={() => whatsapp(created)}><MessageSquare size={15} className="mr-1" /> WhatsApp</Button>
          <Button variant="outline" className="rounded-sm" data-testid="pos-whatsapp-api" disabled={sendingApi} onClick={() => whatsappApi(created)}><Send size={15} className="mr-1" /> {sendingApi ? "Sending…" : "WhatsApp API"}</Button>
          <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="pos-new" onClick={() => { setCreated(null); setRows([blankRow()]); setClient({ id: null, name: "", mobile: "" }); setHistory(null); setPayments([{ method: "Cash", amount: "" }]); setOverallDiscount(0); }}>New Bill</Button>
          <Button variant="ghost" onClick={() => navigate("/billing")}>Back to List</Button>
        </div>
      </div>
    );
  }

  const custHistoryId = new URLSearchParams(location.search).get("cust_history");
  const closeCustHistory = () => { if (custHistoryId) navigate(-1); };

  return (<>
    <CustomerFullHistory customerId={custHistoryId} onClose={closeCustHistory} />
    <div className="space-y-4">
      {/* Quick Restock dialog (for low/out sale products in POS) */}
      <Dialog open={!!restock} onOpenChange={(o) => !o && setRestock(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Quick Restock</DialogTitle></DialogHeader>
          {restock && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">{restock.name} · current stock {restock.stock_qty ?? 0}</p>
              <div>
                <Label className="text-xs">Quantity to add</Label>
                <Input type="number" step="any" value={restockQty} autoFocus
                  onChange={(e) => setRestockQty(e.target.value)}
                  className="bg-secondary mt-1" data-testid="pos-restock-qty"
                  onKeyDown={(e) => { if (e.key === "Enter") submitRestock(); }} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={submitRestock} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="pos-restock-save">Add Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/billing")} data-testid="pos-back"><ArrowLeft size={18} /></Button>
          <Logo className="text-lg" />
          <span className="text-xs text-muted-foreground hidden sm:block">{editId ? "Edit Invoice" : "New Invoice"} · {istNow().toLocaleDateString("en-IN")}</span>
        </div>
        {isAdmin ? (
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="w-56 h-9 bg-secondary" data-testid="pos-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
            <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        ) : <Badge variant="outline" className="border-primary text-primary">{branch?.name}</Badge>}
      </div>

      {/* Back-Date Billing (permission-gated; new bills only) */}
      {!editId && canBackdate && (
        <Card className="p-4 bg-card border-border" data-testid="backdate-billing-card">
          <div className="flex flex-wrap items-center gap-3">
            <Label className="text-xs flex items-center gap-1"><CalendarIcon size={12} /> Billing Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="rounded-sm justify-start bg-secondary w-52" data-testid="backdate-bill-date">
                  <CalendarIcon size={15} className="mr-2" />
                  {billDate && billDate !== todayStr ? format(new Date(`${billDate}T00:00:00`), "dd MMM yyyy") : "Today"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                <Calendar mode="single"
                  selected={billDate ? new Date(`${billDate}T00:00:00`) : istNow()}
                  onSelect={(d) => applyBackdate(d)}
                  disabled={(d) => d > istNow()} initialFocus />
              </PopoverContent>
            </Popover>
            {billDate && billDate !== todayStr && (
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => applyBackdate(null)} data-testid="backdate-reset">Reset to Today</Button>
            )}
          </div>
          {billDate && billDate !== todayStr && (
            <div className="mt-3 flex items-center gap-2 rounded-sm border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-amber-300 text-sm" data-testid="backdate-warning">
              <AlertTriangle size={15} /> Back-Date Billing Active — {format(new Date(`${billDate}T00:00:00`), "dd MMM yyyy")}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 items-start">
        {/* MAIN */}
        <div className="space-y-4">
          {/* bill date & time (edit mode) */}
          {editId && (
            <Card className="p-4 bg-card border-border" data-testid="edit-datetime-card">
              <Label className="text-xs flex items-center gap-1 mb-2"><CalendarIcon size={12} /> Bill Date & Time</Label>
              <div className="flex flex-wrap items-center gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="rounded-sm justify-start bg-secondary w-52" data-testid="edit-bill-date">
                      <CalendarIcon size={15} className="mr-2" />
                      {billDate ? format(new Date(`${billDate}T00:00:00`), "dd MMM yyyy") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                    <Calendar mode="single"
                      selected={billDate ? new Date(`${billDate}T00:00:00`) : undefined}
                      onSelect={(d) => d && setBillDate(format(d, "yyyy-MM-dd"))} initialFocus />
                  </PopoverContent>
                </Popover>
                <div className="relative">
                  <Clock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <Input type="time" value={billTime} onChange={(e) => setBillTime(e.target.value)}
                    className="bg-secondary w-40 pl-9" data-testid="edit-bill-time" />
                </div>
              </div>
            </Card>
          )}

          {/* customer */}
          <Card className="p-4 bg-card border-border">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <Label className="text-xs flex items-center gap-1"><User size={12} /> Client Name</Label>
                <Input value={client.name} autoComplete="off"
                  onChange={(e) => setClient({ id: null, name: e.target.value, mobile: client.mobile })}
                  onFocus={() => { if (nameSug.length) setShowNameSug(true); }}
                  onBlur={() => setTimeout(() => setShowNameSug(false), 200)}
                  placeholder="Type 3+ letters to search" className="bg-secondary mt-1" data-testid="pos-client-name" />
                {showNameSug && (
                  <div className="absolute z-30 left-0 right-0 mt-1 bg-[#0f1c16] border border-border rounded-sm shadow-2xl overflow-hidden" data-testid="pos-name-suggestions">
                    {nameSearching && <div className="p-3 text-xs text-muted-foreground">Searching…</div>}
                    {!nameSearching && nameSug.map((s) => (
                      <button key={s.id} type="button" onClick={() => selectCustomer(s.id)} data-testid={`pos-name-suggest-${s.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-secondary border-b border-border/50 flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">{s.name}</span>
                        <span className="text-primary text-sm font-mono">{s.mobile}</span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Last: {s.last_visit || "—"}</span>
                      </button>
                    ))}
                    {!nameSearching && !nameSug.length && (
                      <div className="p-3" data-testid="pos-name-not-found">
                        <p className="text-sm text-muted-foreground">Customer Not Found</p>
                        <Button size="sm" variant="ghost" onClick={openAdd} data-testid="pos-name-add-customer" className="mt-2 rounded-sm text-primary"><Plus size={14} className="mr-1" /> Add New Customer</Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="relative">
                <Label className="text-xs">Contact No.</Label>
                <Input value={client.mobile} autoComplete="off"
                  onChange={(e) => setClient({ id: null, name: client.name, mobile: e.target.value })}
                  onFocus={() => { if (suggestions.length) setShowSuggest(true); }}
                  placeholder="Type 4+ digits to search" className="bg-secondary mt-1" data-testid="pos-client-mobile" />
                {showSuggest && (
                  <div className="absolute z-30 left-0 right-0 mt-1 bg-[#0f1c16] border border-border rounded-sm shadow-2xl overflow-hidden" data-testid="pos-suggestions">
                    {searching && <div className="p-3 text-xs text-muted-foreground">Searching…</div>}
                    {!searching && suggestions.map((s) => (
                      <button key={s.id} type="button" onClick={() => selectCustomer(s.id)} data-testid={`pos-suggest-${s.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-secondary border-b border-border/50 flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">{s.name}</span>
                        <span className="text-primary text-sm font-mono">{s.mobile}</span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Last: {s.last_visit || "—"}</span>
                      </button>
                    ))}
                    {!searching && !suggestions.length && (
                      <div className="p-3" data-testid="pos-not-found">
                        <p className="text-sm text-muted-foreground">Customer Not Found</p>
                        <Button size="sm" variant="ghost" onClick={openAdd} data-testid="pos-add-customer" className="mt-2 rounded-sm text-primary"><Plus size={14} className="mr-1" /> Add New Customer</Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {(!client.id || (history?.total_visits || 0) === 0) && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-secondary/40 p-2" data-testid="pos-referral-section">
              <span className="text-xs text-muted-foreground">Referral Code <span className="text-primary/70">(new customer)</span></span>
              <Input value={referralCode} onChange={(e) => setReferralCode(e.target.value)} placeholder="DEF-XXXXXX" disabled={!!referralInfo} className="h-8 w-44 bg-secondary uppercase" data-testid="pos-referral-input" />
              {referralInfo
                ? <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => { setReferralInfo(null); setReferralCode(""); }} data-testid="pos-referral-clear">Clear</Button>
                : <Button size="sm" className="h-8 rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="pos-referral-apply" onClick={async () => {
                    try { const { data } = await api.post("/referrals/validate", { code: referralCode, customer_id: client.id || null }); setReferralInfo(data); toast.success(`Referral by ${data.referrer_name} — ${data.discount_percent}% off first bill`); }
                    catch (e) { setReferralInfo(null); toast.error(apiError(e.response?.data?.detail)); }
                  }}>Apply</Button>}
              {referralInfo && <span className="text-xs text-green-300" data-testid="pos-referral-ok">Referral Applied ✓ · Referrer: {referralInfo.referrer_name} · New Customer Discount: {referralInfo.discount_percent}% · Referrer Reward: {referralInfo.cashback_percent}%</span>}
            </div>
            )}
            {sourceStatus.needs_source && (
              <div className="mt-3 p-3 rounded-sm border border-primary/40 bg-primary/5" data-testid="pos-marketing-source">
                <Label className="text-xs text-primary">Marketing Source <span className="text-red-400">*</span> <span className="text-muted-foreground font-normal">(new customer — required for the first bill)</span></Label>
                <Select value={marketingSource} onValueChange={setMarketingSource}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="pos-marketing-source-select"><SelectValue placeholder="How did they hear about us?" /></SelectTrigger>
                  <SelectContent>
                    {MARKETING_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {client.id && (
              <div className="mt-3" data-testid="pos-selected-customer">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">Selected · <span className="text-foreground font-medium">{client.name}</span> · {client.mobile}</span>
                  <Button size="sm" variant="outline" className="h-7 rounded-sm border-primary/50 text-primary" onClick={openEditCust} data-testid="pos-edit-customer"><Pencil size={13} className="mr-1" /> Edit</Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Info label="Total Visits" value={history?.total_visits || 0} />
                  <Info label="Last Visit" value={history?.last_visit || "—"} />
                  <Info label="Wallet" value={rupee(history?.wallet_balance || 0)} />
                  <Info label="Cashback" value={rupee(history?.cashback_balance || 0)} />
                </div>
              </div>
            )}
          </Card>

          {/* edit reason */}
          <Dialog open={reasonOpen} onOpenChange={setReasonOpen}>
            <DialogContent className="bg-card border-border max-w-md">
              <DialogHeader><DialogTitle className="font-display text-2xl">Reason for Edit</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">Add a short reason for changing this bill. It will be saved in the Audit Log.</p>
              <Input value={editReason} onChange={(e) => setEditReason(e.target.value)} autoFocus
                placeholder="e.g. Corrected service / wrong staff / date fix"
                className="bg-secondary mt-1" data-testid="pos-edit-reason" />
              <DialogFooter>
                <Button variant="ghost" className="rounded-sm" onClick={() => setReasonOpen(false)} data-testid="pos-edit-reason-cancel">Cancel</Button>
                <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" disabled={!editReason.trim() || saving}
                  onClick={() => { setReasonOpen(false); submit(null, editReason.trim()); }} data-testid="pos-edit-reason-save">Save Changes</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>


          {/* add new customer */}
          <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setEditCustId(null); }}>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle className="font-display text-2xl">{editCustId ? "Edit Customer" : "Add New Customer"}</DialogTitle></DialogHeader>
              {editCustId && <p className="text-xs text-muted-foreground -mt-2">Corrections update the same customer everywhere. All bills, visits, wallet and history stay linked.</p>}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Full Name *</Label><Input value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} className="bg-secondary mt-1" data-testid="pos-newcust-name" /></div>
                  <div><Label className="text-xs">Mobile *</Label><Input value={newCust.mobile} onChange={(e) => setNewCust({ ...newCust, mobile: e.target.value })} className="bg-secondary mt-1" data-testid="pos-newcust-mobile" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Gender</Label>
                    <Select value={newCust.gender} onValueChange={(v) => setNewCust({ ...newCust, gender: v })}>
                      <SelectTrigger className="bg-secondary mt-1" data-testid="pos-newcust-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{["Male", "Female", "Other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={newCust.dob} onChange={(e) => setNewCust({ ...newCust, dob: e.target.value })} className="bg-secondary mt-1" data-testid="pos-newcust-dob" /></div>
                </div>
                <div><Label className="text-xs">Email</Label><Input value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} className="bg-secondary mt-1" data-testid="pos-newcust-email" /></div>
                <div><Label className="text-xs">Address</Label><Input value={newCust.address} onChange={(e) => setNewCust({ ...newCust, address: e.target.value })} className="bg-secondary mt-1" data-testid="pos-newcust-address" /></div>
                <div><Label className="text-xs">Notes</Label><Input value={newCust.notes} onChange={(e) => setNewCust({ ...newCust, notes: e.target.value })} className="bg-secondary mt-1" data-testid="pos-newcust-notes" /></div>
              </div>
              <DialogFooter><Button onClick={saveNew} data-testid="pos-newcust-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">{editCustId ? "Save Changes" : "Save Customer"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          {/* table */}
          <Card className="bg-card border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-[#12201A] text-muted-foreground text-left">
                    <th className="p-3 font-medium w-8">#</th>
                    <th className="p-3 font-medium">Service</th>
                    <th className="p-3 font-medium w-28">Qty</th>
                    <th className="p-3 font-medium w-36">Staff</th>
                    <th className="p-3 font-medium w-20">Duration</th>
                    <th className="p-3 font-medium w-24 text-right">Rate</th>
                    <th className="p-3 font-medium w-20">Disc %</th>
                    <th className="p-3 font-medium w-24">Disc ₹</th>
                    <th className="p-3 font-medium w-28 text-right">Total</th>
                    <th className="p-3 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((it, i) => {
                    const c = lineCalc(it);
                    const pctShow = it.discount_type === "percent" ? it.discount_value : (c.gross ? +(c.disc / c.gross * 100).toFixed(1) : 0);
                    const amtShow = it.discount_type === "amount" ? it.discount_value : +c.disc.toFixed(0);
                    return (
                      <tr key={i} className={`border-t border-border ${staffErrorRows.includes(i) ? "bg-destructive/10" : ""}`} data-testid={`pos-row-${i}`}>
                        <td className="p-2 text-center text-muted-foreground">{i + 1}</td>
                        <td className="p-2">
                          {it.type === "product"
                            ? <ProductSearch row={it} branchId={branchId} onPick={(p) => pickProduct(i, p)} idx={i} onRestock={isAdmin ? (p) => { setRestock(p); setRestockQty(""); } : undefined} />
                            : (it.type === "wallet_topup" || it.type === "membership")
                              ? <div className="flex items-center gap-2 py-1.5" data-testid={`pos-special-${i}`}><span className="text-xs px-2 py-0.5 rounded-sm bg-primary/15 text-primary">{it.category}</span><span className="font-medium">{it.name}</span></div>
                              : <ServiceSearch row={it} branchId={branchId} onPick={(svc) => pickService(i, svc)} idx={i} />}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 bg-secondary" onClick={() => setRow(i, { qty: Math.max(1, Number(it.qty || 1) - 1) })} data-testid={`pos-qty-dec-${i}`}><Minus size={13} /></Button>
                            <span className="w-7 text-center font-medium" data-testid={`pos-qty-${i}`}>{it.qty}</span>
                            <Button size="icon" variant="ghost" className="h-7 w-7 bg-secondary" onClick={() => setRow(i, { qty: Number(it.qty || 1) + 1 })} data-testid={`pos-qty-inc-${i}`}><Plus size={13} /></Button>
                          </div>
                        </td>
                        <td className="p-2">
                          <div className="flex flex-col gap-1">
                            <Select value={it.staff_id} onValueChange={(v) => { setRow(i, { staff_id: v }); setStaffErrorRows((e) => e.filter((x) => x !== i)); }}>
                              <SelectTrigger className={`bg-secondary h-9 ${staffErrorRows.includes(i) ? "ring-1 ring-destructive border-destructive" : ""}`} data-testid={`pos-staff-${i}`}><SelectValue placeholder="Staff" /></SelectTrigger>
                              <SelectContent>{staffOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                            </Select>
                            {it.type === "service" && (
                              <Button type="button" size="sm" variant="ghost" disabled={!prevStaffId(i)} onClick={() => copyPrevStaff(i)}
                                className="h-6 px-1.5 text-[11px] justify-start text-muted-foreground hover:text-primary disabled:opacity-40" data-testid={`pos-copy-prev-staff-${i}`}>
                                <Copy size={11} className="mr-1" /> Copy previous
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">{it.duration ? `${it.duration} min` : "—"}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          {it.editable_price
                            ? <Input type="number" value={it.price} onChange={(e) => setRow(i, { price: e.target.value, actual_price: e.target.value })} className="bg-secondary h-9 w-24 text-right" data-testid={`pos-rate-${i}`} />
                            : rupee(it.price)}
                        </td>
                        <td className="p-2">{(it.editable_price || it.freedom_group)
                          ? <span className="block h-9 leading-9 text-center text-muted-foreground" data-testid={`pos-nodisc-pct-${i}`}>—</span>
                          : <Input type="number" value={pctShow} onChange={(e) => setRow(i, { discount_type: "percent", discount_value: e.target.value })} className="bg-secondary h-9" data-testid={`pos-discpct-${i}`} />}</td>
                        <td className="p-2">{(it.editable_price || it.freedom_group)
                          ? <span className="block h-9 leading-9 text-center text-muted-foreground" data-testid={`pos-nodisc-amt-${i}`}>—</span>
                          : <Input type="number" value={amtShow} onChange={(e) => setRow(i, { discount_type: "amount", discount_value: e.target.value })} className="bg-secondary h-9" data-testid={`pos-discamt-${i}`} />}</td>
                        <td className="p-2 text-right font-medium text-primary" data-testid={`pos-total-${i}`}>{rupee(c.net)}</td>
                        <td className="p-2 whitespace-nowrap">
                          {it.type === "service" && it.service_id && (
                            <Button size="icon" variant="ghost" title="Clone line (new stylist)" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => cloneRow(i)} data-testid={`pos-clone-${i}`}><Copy size={14} /></Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => delRow(i)} data-testid={`pos-del-${i}`}><Trash2 size={14} /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-border flex flex-wrap gap-2">
              <Button size="sm" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" onClick={() => setSvcPickerOpen(true)} data-testid="pos-select-services-btn"><ListChecks size={15} className="mr-1" /> Select Services</Button>
              {fp.enabled && fp.slabs?.length > 0 && <Button variant="outline" size="sm" className="rounded-sm border-primary/50 text-primary" onClick={openFreedom} data-testid="pos-freedom-btn"><Gift size={15} className="mr-1" /> Freedom Package</Button>}
              {comboPkgs.length > 0 && <Button variant="outline" size="sm" className="rounded-sm border-primary/50 text-primary" onClick={() => setPkgPickerOpen(true)} data-testid="pos-package-btn"><Package size={15} className="mr-1" /> Package</Button>}
              <Button variant="outline" size="sm" className="rounded-sm" onClick={addRow} data-testid="pos-add-service-row"><Plus size={15} className="mr-1" /> Add Service</Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={addProductRow} data-testid="pos-add-product-row"><Package size={15} className="mr-1" /> Add Product</Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={addMembership} data-testid="pos-add-membership"><Plus size={15} className="mr-1" /> Membership</Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={addWalletTopup} data-testid="pos-add-wallet"><Plus size={15} className="mr-1" /> Wallet Top-up</Button>
            </div>
          </Card>
          <ServiceMultiSelect open={svcPickerOpen} onOpenChange={setSvcPickerOpen} branchId={branchId}
            onAdd={addServiceFromPicker} />

          {/* Combo package picker */}
          <Dialog open={pkgPickerOpen} onOpenChange={setPkgPickerOpen}>
            <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="pos-package-dialog">
              <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Package size={18} className="text-primary" /> Add Package</DialogTitle></DialogHeader>
              <p className="text-xs text-muted-foreground -mt-2">Adds the package as a single line at its offer price. Included services stay linked to the package.</p>
              <div className="space-y-3">
                {comboPkgs.map((p) => (
                  <button key={p.id} type="button" onClick={() => addPackageLine(p)} data-testid={`pos-package-opt-${p.id}`}
                    className="w-full text-left rounded-md border border-border bg-secondary/40 hover:border-primary p-3 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{p.name}</span>
                      <span className="text-primary font-semibold">{rupee(p.offer_price)}{p.actual_price > p.offer_price && <span className="text-xs text-muted-foreground line-through ml-2">{rupee(p.actual_price)}</span>}</span>
                    </div>
                    {(p.services || []).length > 0 && <p className="text-xs text-primary/80 mt-1">{p.services.map((s) => s.name).join("  +  ")}</p>}
                    {p.duration > 0 && <p className="text-xs text-muted-foreground mt-0.5">{p.duration} min</p>}
                  </button>
                ))}
                {!comboPkgs.length && <p className="text-sm text-muted-foreground">No active packages.</p>}
              </div>
            </DialogContent>
          </Dialog>

          {/* Freedom Package builder */}
          <Dialog open={fpOpen} onOpenChange={setFpOpen}>
            <DialogContent className="bg-card border-border sm:max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid="pos-freedom-dialog">
              <DialogHeader className="p-4 pb-3 border-b border-border text-left shrink-0 min-w-0">
                <DialogTitle className="font-display text-xl flex items-center gap-2"><Gift size={18} className="text-primary" /> Freedom Package</DialogTitle>
                <div className="flex flex-wrap gap-2 mt-3">
                  {fp.slabs.map((s) => (
                    <button key={s.count} type="button" onClick={() => { setFpSlab(s.count); setFpSel({}); }} data-testid={`pos-fp-slab-${s.count}`}
                      className={`text-xs px-3 py-1.5 rounded-full border ${fpSlab === s.count ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>Any {s.count} · {rupee(s.price)}</button>
                  ))}
                </div>
                {fpSlabObj && <p className="text-xs text-muted-foreground mt-2" data-testid="pos-fp-hint">Choose exactly {fpSlabObj.count} services, each with a staff. Customer pays {rupee(fpSlabObj.price)} total.</p>}
                <Input value={fpQ} onChange={(e) => setFpQ(e.target.value)} placeholder="Search services…" className="bg-secondary h-9 mt-3" data-testid="pos-fp-search" />
              </DialogHeader>
              <div className="flex-1 min-h-0 overflow-y-auto p-2" data-testid="pos-fp-list">
                {fp.services.filter((s) => { const t = fpQ.trim().toLowerCase(); return !t || (s.name || "").toLowerCase().includes(t) || (s.category || "").toLowerCase().includes(t); }).map((s) => {
                  const selected = fpSel[s.id] !== undefined;
                  const atLimit = fpSlabObj && fpSelIds.length >= fpSlabObj.count && !selected;
                  return (
                    <div key={s.id} className={`rounded-md border px-3 py-2 mb-2 ${selected ? "border-primary bg-primary/5" : "border-border"} ${atLimit ? "opacity-50" : ""}`} data-testid={`pos-fp-svc-${s.id}`}>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" checked={selected} disabled={atLimit} onChange={() => toggleFpSel(s)} className="h-4 w-4 accent-[#C9A24B]" data-testid={`pos-fp-toggle-${s.id}`} />
                        <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{s.name}</p><p className="text-xs text-muted-foreground">{s.category} · {s.duration} min</p></div>
                        <span className="text-sm text-primary shrink-0">{rupee(s.actual_price)}</span>
                      </div>
                      {selected && (
                        <div className="mt-2 pl-7">
                          <Select value={fpSel[s.id] || ""} onValueChange={(v) => setFpSel((m) => ({ ...m, [s.id]: v }))}>
                            <SelectTrigger className="bg-secondary h-9" data-testid={`pos-fp-staff-${s.id}`}><SelectValue placeholder="Select staff for this service" /></SelectTrigger>
                            <SelectContent>{fpStaffFor(s.id).map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!fp.services.length && <p className="text-sm text-muted-foreground p-4 text-center">No eligible services configured. Add them in Service Management → Freedom Package.</p>}
              </div>
              <DialogFooter className="p-3 border-t border-border flex-col gap-2 shrink-0 min-w-0">
                <div className="flex items-center justify-between w-full text-xs">
                  <span className="text-muted-foreground" data-testid="pos-fp-count">{fpSelIds.length}{fpSlabObj ? `/${fpSlabObj.count}` : ""} selected</span>
                  <span>Actual <span className="line-through text-muted-foreground">{rupee(fpActualTotal)}</span> → Package <span className="text-primary font-medium">{rupee(fpSlabObj?.price || 0)}</span></span>
                </div>
                <Button onClick={addFreedom} disabled={!fpSlabObj || fpSelIds.length !== fpSlabObj.count} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="pos-fp-add">ADD PACKAGE TO BILL{fpSlabObj ? ` (${rupee(fpSlabObj.price)})` : ""}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {freedomGroups.length > 0 && (
            <Card className="p-4 bg-primary/5 border-primary/30 space-y-2" data-testid="pos-freedom-summary">
              {freedomGroups.map((fg) => (
                <div key={fg.g} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`pos-freedom-summary-${fg.g}`}>
                  <span className="flex items-center gap-2"><Gift size={15} className="text-primary" /> Freedom Package · Any {fg.count} — {fg.names.join(", ")}</span>
                  <span>Actual Value <span className="line-through text-muted-foreground">{rupee(fg.actual)}</span> · Final Payable <span className="text-primary font-medium">{rupee(fg.slab)}</span></span>
                </div>
              ))}
            </Card>
          )}

          {/* payment */}
          <Card className="p-4 bg-card border-border">
            <Label className="text-xs">Payment Method</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {["Cash", "Card", "UPI", "Wallet", "Cashback", "Bank Transfer"].map((m) => (
                <Button key={m} size="sm" variant="ghost" data-testid={`pos-pay-${m.toLowerCase().replace(/ /g, "-")}`}
                  onClick={() => setPayments([{ method: m, amount: calc.grand.toFixed(2) }])}
                  className={`rounded-sm ${payments.length === 1 && payments[0].method === m ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{m}</Button>
              ))}
              <Button size="sm" variant="ghost" data-testid="pos-pay-split"
                onClick={() => setPayments((p) => [...p, { method: "UPI", amount: "" }])}
                className="rounded-sm bg-secondary">+ Split</Button>
            </div>
            <div className="space-y-2 mt-3">
              {payments.map((p, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select value={p.method} onValueChange={(v) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, method: v } : x))}>
                    <SelectTrigger className="w-32 h-9 bg-secondary"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Cash", "Card", "UPI", "Wallet", "Cashback", "Bank Transfer"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex-1">
                    <Input type="number" value={p.amount} placeholder="Amount received" data-testid={`pos-amount-${i}`}
                      onChange={(e) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))}
                      className={`bg-secondary h-9 w-full ${p.method === "Wallet" && walletOver ? "text-red-400 border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500" : ""}`} />
                    {p.method === "Wallet" && walletOver && <p className="text-xs text-red-400 mt-1" data-testid={`pos-wallet-error-${i}`}>Insufficient wallet balance. Available balance: {rupee(walletAvail)}</p>}
                  </div>
                  {payments.length > 1 && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setPayments((ps) => ps.filter((_, idx) => idx !== i))}><Trash2 size={14} /></Button>}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm mt-3 pt-3 border-t border-border">
              <span className="text-muted-foreground">Received</span><span>{rupee(received)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Balance</span><span className={balance > 0.01 ? "text-orange-300" : "text-green-300"} data-testid="pos-balance">{rupee(balance)}</span>
            </div>
            {underpaid && <p className="text-xs text-orange-300 mt-2" data-testid="pos-underpaid-hint">Enter full payment ({rupee(calc.grand)}) to complete the invoice, or Save as Draft.</p>}
          </Card>

          <div className="sticky bottom-0 -mx-1 flex flex-wrap gap-2 bg-background/90 backdrop-blur-sm border-t border-border pt-3 pb-1">
            <Button variant="outline" className="rounded-sm flex-1 min-w-[120px]" data-testid="pos-review"
              onClick={() => { if (validateStaff()) toast.message(`${rows.filter((r) => r.service_id || r.product_id).length} item(s) · Grand Total ${rupee(calc.grand)}`); }}>
              <Eye size={15} className="mr-1" /> Review</Button>
            {!draftId && !editId && <Button variant="outline" className="rounded-sm flex-1 min-w-[120px]" disabled={saving} onClick={() => submit("draft")} data-testid="pos-save-draft"><Save size={15} className="mr-1" /> Save Draft</Button>}
            <Button className="rounded-sm flex-1 min-w-[160px] bg-primary text-primary-foreground hover:bg-[#D4AF37]" disabled={saving || walletOver || underpaid} onClick={() => editId ? (setEditReason(""), setReasonOpen(true)) : submit(null)} data-testid="pos-generate"><CreditCard size={15} className="mr-1" /> {editId ? "Update Invoice" : draftId ? "Convert to Invoice" : "Checkout"}</Button>
          </div>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-4 lg:sticky lg:top-4 self-start">
          {/* summary */}
          <Card className="p-4 bg-card border-border">
            <h3 className="font-display text-lg mb-3">Summary</h3>
            <div className="space-y-1.5 text-sm">
              <Row label="Subtotal" value={rupee(calc.subtotal)} />
              {calc.savings > 0 && <Row label="You Saved" value={`− ${rupee(calc.savings)}`} cls="text-green-300" />}
              <Row label="Line Discount" value={`− ${rupee(calc.itemDiscount)}`} cls="text-destructive" />
              <Row label="Net Total" value={rupee(calc.net)} />
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Bill Discount ₹</span>
                <Input type="number" value={overallDiscount} onChange={(e) => setOverallDiscount(e.target.value)} className="w-20 h-8 bg-secondary text-right" data-testid="pos-bill-discount" />
              </div>
              <div className="rounded-md border border-border/60 bg-secondary/40 p-2" data-testid="pos-coupon-section">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Coupon Code</span>
                  <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="DEFINE15" disabled={!!couponInfo} className="h-8 flex-1 bg-secondary uppercase font-mono" data-testid="pos-coupon-input" />
                  {couponInfo
                    ? <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => { setCouponInfo(null); setCouponCode(""); }} data-testid="pos-coupon-clear">Remove</Button>
                    : <Button size="sm" className="h-8 rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" disabled={!couponCode.trim()} data-testid="pos-coupon-apply" onClick={async () => {
                        try {
                          const { data } = await api.post("/coupons/validate", { code: couponCode.trim(), customer_id: client.id || null, customer_mobile: client.mobile || "", net_service: calc.net });
                          setCouponInfo(data); toast.success(`Coupon ${data.code} applied — ${data.label} off`);
                        } catch (e) { setCouponInfo(null); toast.error(apiError(e.response?.data?.detail)); }
                      }}>Apply</Button>}
                </div>
                {couponInfo && <p className="text-xs text-green-300 mt-1" data-testid="pos-coupon-ok">✓ {couponInfo.name} ({couponInfo.code}) · {couponInfo.label} off</p>}
              </div>
              {couponInfo && <Row label={`Coupon Discount (${couponInfo.code})`} value={`− ${rupee(calc.couponDisc)}`} cls="text-green-300" />}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">GST % (incl.)</span>
                <Input type="number" value={gst} onChange={(e) => setGst(e.target.value)} className="w-20 h-8 bg-secondary text-right" data-testid="pos-gst" />
              </div>
              {referralActive && <Row label={`Referral Discount (${referralInfo.discount_percent}%)`} value={`− ${rupee(calc.referralDisc)}`} cls="text-green-300" />}
              {referralInfo && !referralActive && <p className="text-xs text-orange-300" data-testid="pos-referral-ineligible">Referral applies to a customer's first bill only — not applied to this customer.</p>}
              <Row label="Taxable Value" value={rupee(calc.taxableValue)} />
              <Row label={`GST (${Number(gst || 0)}% incl.)`} value={rupee(calc.tax)} />
              <div className="flex justify-between font-display text-2xl pt-2 mt-1 border-t border-border">
                <span>Grand Total</span><span className="text-primary" data-testid="pos-grand-total">{rupee(calc.grand)}</span>
              </div>
            </div>
          </Card>

          {/* staff service summary */}
          <Card className="p-4 bg-card border-border">
            <h3 className="font-display text-lg mb-3">Staff Service Summary</h3>
            <div className="space-y-2">
              {summary.length ? summary.map((s, i) => (
                <div key={i} className="text-sm border-b border-border/50 pb-2" data-testid={`pos-summary-${i}`}>
                  <p className="font-medium">{i + 1}. {s.staff}</p>
                  <div className="flex justify-between text-muted-foreground text-xs">
                    <span>{s.service}</span><span className="text-primary">{rupee(s.net)}</span>
                  </div>
                </div>
              )) : <p className="text-muted-foreground text-sm">Assign staff to services to see the incentive summary.</p>}
            </div>
          </Card>

          {/* customer history */}
          <Card className="p-4 bg-card border-border">
            <h3 className="font-display text-lg mb-3 flex items-center gap-1"><History size={15} /> Customer History</h3>
            {history ? (
              <div className="space-y-3 text-sm" data-testid="pos-history">
                <div>
                  <p className="font-medium">{history.name}</p>
                  <p className="text-xs text-primary font-mono">{history.mobile}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Stat label="Total Visits" value={history.total_visits || 0} />
                  <Stat label="Total Spend" value={rupee(history.total_spending)} />
                  <Stat label="Wallet" value={rupee(history.wallet_balance || 0)} />
                  <Stat label="Cashback" value={rupee(history.cashback_balance || 0)} />
                </div>
                <p className="text-xs text-muted-foreground">Last visit: {history.last_visit || "—"}</p>
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">Recent Visits</p>
                  <div className="max-h-72 overflow-y-auto pr-1 space-y-3">
                    {history.invoices?.slice(0, 5).map((iv) => (
                      <div key={iv.id} className="border-b border-border/40 pb-2" data-testid={`pos-hist-inv-${iv.id}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">{iv.bill_date}</span>
                          <span className="text-[10px] font-mono text-muted-foreground">{iv.invoice_no}</span>
                        </div>
                        {(iv.items || []).slice(0, 5).map((it, j) => (
                          <div key={j} className="flex justify-between text-xs mt-0.5">
                            <span className="truncate pr-2">{it.name}{it.staff_name ? ` — ${it.staff_name}` : ""}{it.qty > 1 ? ` ×${it.qty}` : ""}</span>
                            <span className="text-muted-foreground whitespace-nowrap">{rupee(it.net_amount ?? it.price)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-xs mt-1">
                          <span className="text-muted-foreground">Bill Total</span>
                          <span className="text-primary font-medium">{rupee(iv.grand_total)}</span>
                        </div>
                      </div>
                    ))}
                    {!history.invoices?.length && <p className="text-xs text-muted-foreground">No previous bills.</p>}
                  </div>
                  {history.id && (
                    <Button variant="ghost" size="sm" className="mt-2 text-primary rounded-sm w-full" data-testid="pos-view-full-history"
                      onClick={() => navigate(`${location.pathname}?cust_history=${history.id}`)}>View Full History</Button>
                  )}
                </div>
              </div>
            ) : <p className="text-muted-foreground text-sm">Search a name or mobile number to view history.</p>}
          </Card>
        </div>
      </div>
    </div>
  </>);
}

function Row({ label, value, cls = "" }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className={cls}>{value}</span></div>;
}
function Stat({ label, value }) {
  return <div className="bg-secondary rounded-sm p-2"><p className="text-xs text-muted-foreground">{label}</p><p className="font-display text-xl">{value}</p></div>;
}
function Info({ label, value }) {
  return <div className="bg-secondary rounded-sm p-2 text-center"><p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p><p className="text-sm font-medium mt-0.5">{value}</p></div>;
}

function ServiceSearch({ row, branchId, onPick, idx }) {
  const [term, setTerm] = useState(row.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState(null);
  const [dirty, setDirty] = useState(false);   // true only while the user is actively typing a search
  const inputRef = useRef(null);

  // Sync the input to the selected service and NEVER treat that as a search (prevents the
  // duplicate service card from popping up for an already-selected service on re-render/focus).
  useEffect(() => { setTerm(row.name || ""); setDirty(false); setOpen(false); setResults([]); }, [row.service_id, row.name]);

  const computePos = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const isMobile = vw < 640;
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    const openUp = !isMobile && spaceBelow < 320 && spaceAbove > spaceBelow;
    const width = isMobile ? vw - 16 : Math.min(Math.max(r.width, 420), vw - 24);
    let left = isMobile ? 8 : r.left;
    if (left + width > vw - 8) left = Math.max(8, vw - width - 8);
    const avail = (openUp ? spaceAbove : spaceBelow) - 16;
    const maxH = Math.max(200, Math.min(avail, vh * 0.7));
    setPos({
      left, width, maxH,
      top: openUp ? undefined : Math.round(r.bottom + 6),
      bottom: openUp ? Math.round(vh - r.top + 6) : undefined,
    });
  };

  useEffect(() => {
    if (!open) return;
    computePos();
    const h = () => computePos();
    window.addEventListener("resize", h);
    window.addEventListener("scroll", h, true);
    return () => { window.removeEventListener("resize", h); window.removeEventListener("scroll", h, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, results, loading]);

  useEffect(() => {
    if (!dirty) { setOpen(false); return; }   // only search on genuine user typing
    const t = (term || "").trim();
    if (row.service_id && t === row.name) { setResults([]); setOpen(false); return; }
    if (t.length < 3) { setResults([]); setOpen(false); return; }
    setLoading(true); setOpen(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/services/search", { params: { q: t, branch_id: branchId } });
        setResults(data);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, branchId, dirty, row.service_id, row.name]);

  const choose = (s) => { onPick(s); setTerm(s.name); setOpen(false); setResults([]); setDirty(false); };

  const panel = (open && pos) ? createPortal(
    <div
      style={{ position: "fixed", left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width, maxHeight: pos.maxH, zIndex: 9999 }}
      className="bg-[#0f1c16] border border-primary/40 rounded-md shadow-2xl overflow-y-auto"
      data-testid={`pos-service-suggest-${idx}`}>
      {loading && <div className="p-4 text-sm text-muted-foreground">Searching…</div>}
      {!loading && results.map((s) => (
        <button key={s.id} type="button" data-testid={`pos-service-opt-${idx}-${s.id}`}
          onMouseDown={(e) => { e.preventDefault(); choose(s); }}
          className="w-full text-left px-4 py-3 hover:bg-primary/15 border-b border-border/40 last:border-0 flex items-center justify-between gap-4 transition-colors">
          <span className="flex flex-col min-w-0">
            <span className="font-medium text-[15px] leading-tight truncate">{s.name}</span>
            <span className="text-xs text-muted-foreground mt-0.5">{s.category}{s.sub_category ? ` · ${s.sub_category}` : ""}</span>
          </span>
          <span className="flex flex-col items-end whitespace-nowrap">
            {s.actual_price && s.actual_price > s.price ? (
              <span className="text-[11px] text-muted-foreground line-through">{rupee(s.actual_price)}</span>
            ) : null}
            <span className="text-primary text-[15px] font-semibold font-mono">{rupee(s.price)}</span>
          </span>
        </button>
      ))}
      {!loading && !results.length && (
        <div className="p-4 text-sm text-muted-foreground" data-testid={`pos-service-none-${idx}`}>No matching service</div>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <div className="relative">
      <Input ref={inputRef} value={term} autoComplete="off"
        onChange={(e) => { setTerm(e.target.value); setDirty(true); }}
        onFocus={() => { if (dirty && results.length) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Type 3+ letters…" className="bg-secondary h-9" data-testid={`pos-service-${idx}`} />
      {panel}
    </div>
  );
}

function ServiceMultiSelect({ open, onOpenChange, branchId, onAdd }) {
  const [all, setAll] = useState([]);
  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [pop, setPop] = useState({});
  const [activeCat, setActiveCat] = useState("ALL");
  const [qty, setQty] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setTerm("");
    setActiveCat("ALL");
    setQty({});
    Promise.all([
      api.get("/services/search", { params: { all: true, branch_id: branchId } }),
      api.get("/service-popularity", { params: { branch_id: branchId } }).catch(() => ({ data: {} })),
    ]).then(([sv, pp]) => { setAll(sv.data || []); setPop(pp.data || {}); })
      .catch(() => { setAll([]); setPop({}); })
      .finally(() => setLoading(false));
  }, [open, branchId]);

  // Categories ordered by total billing popularity (most-used category first), ALL pinned first.
  const categories = useMemo(() => {
    const totals = {};
    all.forEach((s) => { const c = s.category || "Other"; totals[c] = (totals[c] || 0) + (pop[s.id] || 0); });
    const cats = Object.keys(totals).sort((a, b) => (totals[b] - totals[a]) || a.localeCompare(b));
    return ["ALL", ...cats];
  }, [all, pop]);

  // Filter by category + search, then order Most-Billed -> Least (stable tie-break by name).
  const visible = useMemo(() => {
    let list = all;
    if (activeCat !== "ALL") list = list.filter((s) => (s.category || "Other") === activeCat);
    const t = term.trim().toLowerCase();
    if (t) {
      const num = Number(t.replace(/[₹,\s]/g, ""));
      const isNum = /\d/.test(t) && !/[a-z]/.test(t) && !isNaN(num);
      list = isNum
        ? list.filter((s) => { const p = Number(s.price) || 0; return p === num || String(p).includes(String(num)) || Math.abs(p - num) <= Math.max(100, num * 0.1); })
        : list.filter((s) => [s.name, s.category, s.sub_category].some((f) => (f || "").toLowerCase().includes(t)));
    }
    return [...list].sort((a, b) => (pop[b.id] || 0) - (pop[a.id] || 0) || (a.name || "").localeCompare(b.name || ""));
  }, [all, activeCat, term, pop]);

  const totalSelected = Object.values(qty).reduce((a, b) => a + b, 0);
  const commit = () => { all.forEach((s) => { const q = qty[s.id] || 0; for (let k = 0; k < q; k++) onAdd(s); }); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid="pos-multiselect-dialog">
        <DialogHeader className="p-4 pb-3 border-b border-border text-left shrink-0 min-w-0">
          <DialogTitle className="font-display text-xl flex items-center gap-2"><ListChecks size={18} className="text-primary" /> Select Services</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">Most-billed services first. Set a quantity for each service, then tap ADD TO BILL to add them all as bill rows.</p>
          <div className="flex gap-1.5 overflow-x-auto mt-3 pb-1 -mx-1 px-1 no-scrollbar min-w-0 max-w-full" data-testid="pos-multiselect-categories">
            {categories.map((c) => (
              <button key={c} type="button" onClick={() => setActiveCat(c)} data-testid={`pos-cat-${c}`}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wide border transition-colors ${activeCat === c ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border hover:text-foreground"}`}>
                {c}
              </button>
            ))}
          </div>
          <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search by name, category or price (e.g. 1500)…"
            className="bg-secondary mt-2 h-9" data-testid="pos-multiselect-search" />
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto p-2" data-testid="pos-multiselect-list">
          {loading && <div className="p-6 text-sm text-muted-foreground text-center">Loading services…</div>}
          {!loading && !visible.length && <div className="p-6 text-sm text-muted-foreground text-center" data-testid="pos-multiselect-empty">No services found.</div>}
          {!loading && visible.map((s) => {
            const q = qty[s.id] || 0;
            const dur = s.duration || 0;
            return (
              <div key={s.id} data-testid={`pos-multiselect-opt-${s.id}`}
                className={`w-full flex items-center gap-3 px-3 py-3 border-b border-border last:border-b-0 ${q > 0 ? "bg-primary/5" : ""}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[14px] leading-tight truncate">{s.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.category || "Other"}</p>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground w-20 justify-center shrink-0"><Clock size={13} className="text-primary" />{dur ? `${dur} min` : "—"}</div>
                <div className="w-16 text-right text-primary text-sm font-semibold shrink-0">{rupee(s.price)}</div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button type="button" size="icon" variant="outline" className="h-7 w-7 rounded-full" disabled={q === 0} onClick={() => setQty((m) => ({ ...m, [s.id]: Math.max(0, (m[s.id] || 0) - 1) }))} data-testid={`pos-multiselect-dec-${s.id}`}><Minus size={13} /></Button>
                  <span className="w-6 text-center text-sm font-semibold" data-testid={`pos-multiselect-qty-${s.id}`}>{q}</span>
                  <Button type="button" size="icon" className="h-7 w-7 rounded-full bg-primary text-primary-foreground hover:bg-[#D4AF37]" onClick={() => setQty((m) => ({ ...m, [s.id]: (m[s.id] || 0) + 1 }))} data-testid={`pos-multiselect-inc-${s.id}`}><Plus size={13} /></Button>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter className="p-3 border-t border-border flex-col gap-2 shrink-0 min-w-0">
          <span className="text-xs text-muted-foreground self-start" data-testid="pos-multiselect-count">{totalSelected} selected</span>
          <Button onClick={commit} data-testid="pos-multiselect-add"
            className="w-full h-12 rounded-md bg-primary text-primary-foreground hover:bg-[#E6C687] font-semibold tracking-wide text-sm">
            {totalSelected > 0 ? `ADD TO BILL (${totalSelected} SERVICE${totalSelected > 1 ? "S" : ""})` : "ADD TO BILL"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductSearch({ row, branchId, onPick, idx, onRestock }) {
  const [term, setTerm] = useState(row.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { setTerm(row.name || ""); }, [row.product_id, row.name]);

  const computePos = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const isMobile = vw < 640;
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    const openUp = !isMobile && spaceBelow < 320 && spaceAbove > spaceBelow;
    const width = isMobile ? vw - 16 : Math.min(Math.max(r.width, 420), vw - 24);
    let left = isMobile ? 8 : r.left;
    if (left + width > vw - 8) left = Math.max(8, vw - width - 8);
    const avail = (openUp ? spaceAbove : spaceBelow) - 16;
    const maxH = Math.max(200, Math.min(avail, vh * 0.7));
    setPos({ left, width, maxH, top: openUp ? undefined : Math.round(r.bottom + 6), bottom: openUp ? Math.round(vh - r.top + 6) : undefined });
  };

  useEffect(() => {
    if (!open) return;
    computePos();
    const h = () => computePos();
    window.addEventListener("resize", h);
    window.addEventListener("scroll", h, true);
    return () => { window.removeEventListener("resize", h); window.removeEventListener("scroll", h, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, results, loading]);

  useEffect(() => {
    const t = (term || "").trim();
    if (row.product_id && t === row.name) { setResults([]); setOpen(false); return; }
    if (t.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true); setOpen(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/products/search", { params: { q: t, branch_id: branchId } });
        setResults(data);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, branchId]);

  const choose = (p) => { onPick(p); setTerm(p.name); setOpen(false); };

  const panel = (open && pos) ? createPortal(
    <div style={{ position: "fixed", left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width, maxHeight: pos.maxH, zIndex: 9999 }}
      className="bg-[#0f1c16] border border-primary/40 rounded-md shadow-2xl overflow-y-auto" data-testid={`pos-product-suggest-${idx}`}>
      {loading && <div className="p-4 text-sm text-muted-foreground">Searching…</div>}
      {!loading && results.map((p) => {
        const low = (p.stock_qty || 0) <= (p.low_stock_threshold || 0);
        const out = (p.stock_qty || 0) <= 0;
        const showRestock = onRestock && p.incentive_eligible && (out || low);
        return (
          <div key={p.id} className="border-b border-border/40 last:border-0 flex items-center">
            <button type="button" data-testid={`pos-product-opt-${idx}-${p.id}`} disabled={out}
              onMouseDown={(e) => { e.preventDefault(); if (!out) choose(p); }}
              className={`flex-1 min-w-0 text-left px-4 py-3 flex items-center justify-between gap-4 transition-colors ${out ? "opacity-50 cursor-not-allowed" : "hover:bg-primary/15"}`}>
              <span className="flex flex-col min-w-0">
                <span className="font-medium text-[15px] leading-tight truncate">{p.name}</span>
                <span className="text-xs text-muted-foreground mt-0.5">
                  {p.brand ? `${p.brand} · ` : ""}{p.category}
                  <span className={`ml-2 ${out ? "text-red-300" : low ? "text-orange-300" : "text-muted-foreground"}`}>{out ? "Out of stock" : `Stock: ${p.stock_qty}${low ? " (low)" : ""}`}</span>
                </span>
              </span>
              <span className="flex flex-col items-end whitespace-nowrap">
                {p.actual_price && p.actual_price > p.price ? <span className="text-[11px] text-muted-foreground line-through">{rupee(p.actual_price)}</span> : null}
                <span className="text-primary text-[15px] font-semibold font-mono">{rupee(p.price)}</span>
              </span>
            </button>
            {showRestock && (
              <button type="button" data-testid={`pos-restock-${idx}-${p.id}`}
                onMouseDown={(e) => { e.preventDefault(); onRestock(p); }}
                className="mr-3 px-2.5 py-1.5 rounded-sm text-xs font-medium bg-primary/20 text-primary hover:bg-primary/30 whitespace-nowrap">+ Restock</button>
            )}
          </div>
        );
      })}
      {!loading && !results.length && (
        <div className="p-4 text-sm text-muted-foreground" data-testid={`pos-product-none-${idx}`}>No matching product</div>
      )}
    </div>, document.body) : null;

  return (
    <div className="relative">
      <Input ref={inputRef} value={term} autoComplete="off"
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Type product name…" className="bg-secondary h-9" data-testid={`pos-product-${idx}`} />
      {panel}
    </div>
  );
}
